/**
 * Application-layer envelope encryption for optional AI reflection.
 *
 * Supabase Edge Function invocation tooling can expose request/response bodies
 * to project operators. Reflection content is therefore encrypted before it
 * reaches the gateway. TLS still protects transport; this additional envelope
 * keeps reflection text and AI replies out of ordinary invocation body views.
 *
 * The RSA public key is safe to ship in the app. Its matching private key must
 * exist only as a backend secret.
 */

const PUBLIC_KEY_B64 = (process.env.REACT_APP_REFLECTION_PUBLIC_KEY_B64 || "").trim();

const bytesToBase64 = (bytes) => {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunk, bytes.length)));
  }
  return btoa(binary);
};

const base64ToBytes = (value) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

let importedPublicKey = null;

const getPublicKey = async () => {
  if (importedPublicKey) return importedPublicKey;
  if (!PUBLIC_KEY_B64) throw new Error("Reflection encryption key is not configured");
  importedPublicKey = await crypto.subtle.importKey(
    "spki",
    base64ToBytes(PUBLIC_KEY_B64),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
  return importedPublicKey;
};

export const reflectionEncryptionAvailable = Boolean(PUBLIC_KEY_B64 && globalThis.crypto?.subtle);

export const encryptReflectionPayload = async (payload) => {
  const rsaKey = await getPublicKey();
  const aesKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const rawAesKey = new Uint8Array(await crypto.subtle.exportKey("raw", aesKey));
  const wrappedKey = new Uint8Array(
    await crypto.subtle.encrypt({ name: "RSA-OAEP" }, rsaKey, rawAesKey)
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, plaintext)
  );

  return {
    envelope: {
      v: 1,
      wrapped_key: bytesToBase64(wrappedKey),
      iv: bytesToBase64(iv),
      ciphertext: bytesToBase64(ciphertext),
    },
    aesKey,
  };
};

export const decryptReflectionResponse = async (envelope, aesKey) => {
  if (envelope?.v !== 1 || !envelope?.iv || !envelope?.ciphertext) {
    throw new Error("Reflection backend returned an invalid encrypted response");
  }
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToBytes(envelope.iv) },
    aesKey,
    base64ToBytes(envelope.ciphertext)
  );
  return JSON.parse(new TextDecoder().decode(plaintext));
};
